import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Button, TextField, Select, MenuItem, FormControl,
  InputLabel, Grid, Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import TransitEnterexitIcon from '@mui/icons-material/TransitEnterexit';
import { Visibility as ViewIcon, CheckCircle as EvalIcon, HowToReg as AdmitIcon, FilterList as FilterIcon, FileDownload as ExportIcon } from "@mui/icons-material";
import { fetchData, putData } from "../../BaseUrl";
import Dynamictablepagination from "../../DynamicComponent/Dynamictablepagination";
import Toast from "../../DynamicComponent/Toast";
import ApplicationDetailDialog from "./ApplicationDetailDialog";
import EvaluationDialog from "./EvaluationDialog";
import AdmissionDialog from "./AdmissionDialog";
import { exportToCSV } from "../../utils/tableUtils";

const STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "pending_evaluation", label: "Pending Evaluation" },
  { value: "evaluation_passed", label: "Evaluation Passed" },
  { value: "evaluation_failed", label: "Evaluation Failed" },
  { value: "admitted", label: "Admitted" },
  { value: "rejected", label: "Rejected" }
];

const GRADE_OPTIONS = [
  { value: "", label: "All Grades" },
  "MunMazhalai", "Mazhalai",
  "Nilai 1", "Nilai 2", "Nilai 3", "Nilai 4",
  "Nilai 5", "Nilai 6", "Nilai 7", "Nilai 8", "Seal of Bi-literacy"
];

const statusColor = {
  pending_evaluation: "#ff9800",
  evaluation_passed: "#2196f3",
  evaluation_failed: "#f44336",
  admitted: "#4caf50",
  rejected: "#f44336"
};

const AdminRegistrations = () => {
  const [applications, setApplications] = useState([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
  const [filters, setFilters] = useState({ status: "", grade: "", search: "" });
  const [selectedApp, setSelectedApp] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [evalOpen, setEvalOpen] = useState(false);
  const [admitOpen, setAdmitOpen] = useState(false);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });

  const loadApplications = useCallback(async (page = 1) => {
    try {
      const params = new URLSearchParams({ page, limit: 10 });
      if (filters.status) params.append("status", filters.status);
      if (filters.grade) params.append("grade", filters.grade);
      if (filters.search) params.append("search", filters.search);

      const result = await fetchData(`admin/admission/applications?${params.toString()}`);
      if (result?.data) {
        setApplications(result.data.applications || []);
        setPagination(result.data.pagination || { total: 0, page: 1, totalPages: 1 });

      }
    } catch (err) {
      setToast({ open: true, severity: "error", message: "Failed to load applications" });
    }
  }, [filters]);

  useEffect(() => { loadApplications(); }, [loadApplications]);

  const handleActionComplete = (message) => {
    setToast({ open: true, severity: "success", message });
    setDetailOpen(false);
    setEvalOpen(false);
    setAdmitOpen(false);
    loadApplications(pagination.page);
  };

  const columns = [
    { label: "App #", field: "applicationNumber" },
    { label: "Reg #", field: "registrationNumber" },
    { label: "Student Name", field: "studentName" },
    { label: "Student Type", field: "studentTypeDisplay" },
    { label: "Requested Grade", field: "gradeApplyingFor" },
    { label: "Assigned Grade", field: "assignedGrade" },
    { label: "Parent", field: "parentName" },
    { label: "Parent Email Id", field: "parentEmail" },
    { label: "Parent Phone No", field: "parentPhone" },
    { label: "Skip Grade", field: "skipGrade" },
    { label: "Request Eval", field: "reqEvalDisplay" },
    { label: "Status", field: "statusChip" },
    { label: "Actions", field: "actions" }
  ];

  const rows = applications.map((app) => {
    const parent1 = app.parents?.find(p => p.parentType === "parent1");
    return {
      ...app,
      registrationNumber: app.registrationNumber || "-",
      studentName: `${app.firstName} ${app.lastName}`,
      studentTypeDisplay: app.studentType === "old" ? "Existing" : "New",
      reqEvalDisplay: app.requestEvaluation || app.requestForEvaluation ? "Yes" : "No",
      gradeApplyingFor: app.gradeApplyingFor + (app.sealOfBiliteracy === true ? " / Seal of Bi-literacy" : ""),
      assignedGrade: app.assignedGrade || "-",
      parentName: parent1 ? `${parent1.firstName} ${parent1.lastName}` : "-",
      parentEmail: app.parentEmail || parent1?.email || "-",
      parentPhone: app.parentPhone || app.parentContactNo || parent1?.phone || parent1?.contactNo || "-",
      skipGrade: app.isSkippedGrade ? (
        <Chip label="Yes" size="small" sx={{ backgroundColor: "#fff3e0", color: "#e65100" }} />
      ) : "No",
      statusChip: (
        <Chip label={app.status.replace(/_/g, " ")} size="small"
          sx={{ backgroundColor: statusColor[app.status] + "20", color: statusColor[app.status], fontWeight: 600, textTransform: "capitalize" }} />
      ),
      actions: (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="View Details">
            <IconButton size="small" onClick={() => { setSelectedApp(app); setDetailOpen(true); }}>
              <ViewIcon fontSize="small" sx={{ color: "#244C77" }} />
            </IconButton>
          </Tooltip>
          {app.status === "pending_evaluation" && (
            <Tooltip title="Evaluate">
              <IconButton size="small" onClick={() => { setSelectedApp(app); setEvalOpen(true); }}>
                <EvalIcon fontSize="small" sx={{ color: "#ff9800" }} />
              </IconButton>
            </Tooltip>
          )}
          {(
            app.status === "evaluation_passed" ||
            app.status === "evaluation_failed"
          ) && (
              <Tooltip title="Admit">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedApp(app);
                    setAdmitOpen(true);
                  }}
                >
                  <AdmitIcon fontSize="small" sx={{ color: "#4caf50" }} />
                </IconButton>
              </Tooltip>
            )}

          {
            (
              app.status === "evaluation_passed" ||
              app.status === "evaluation_failed"
            )
            && (
              <Tooltip title="Withdraw">
                <IconButton
                  size="small"
                  onClick={() => {
                    setSelectedApp(app);
                    setWithdrawOpen(true);
                  }}
                >
                  <TransitEnterexitIcon fontSize="small" sx={{ color: "#af534c" }} />
                </IconButton>
              </Tooltip>
            )}

        </Box>
      )
    };
  });

  const handleExport = () => {
    exportToCSV(columns, rows, "student_registrations.csv");
    setToast({ open: true, severity: "success", message: "Export started..." });
  };


  const handleWithdraw = async () => {
    if (!selectedApp) return;
    try {
      const result = "withdrawal";
      await putData(`admin/admission/applications/${selectedApp.id}/evaluate`, {
        result,
      });
      setToast({ open: true, severity: "success", message: "Application withdrawn successfully" });
      setWithdrawOpen(false);
      loadApplications(pagination.page);
    } catch (err) {
      setToast({ open: true, severity: "error", message: "Failed to withdraw application" });
    }
  };

  // console.log("rows",rows)

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary" mb={1}>Pages / Registration Management</Typography>
      <Typography variant="h5" fontWeight="bold" mb={0.5}>Registration Management</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>List of Students Registration</Typography>

      {/* Filters */}
      <Grid container spacing={2} mb={3} alignItems="center">
        <Grid item xs={12} sm={3}>
          <TextField size="small" fullWidth placeholder="Search by name or app #"
            value={filters.search} onChange={(e) => setFilters(p => ({ ...p, search: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && loadApplications()} />
        </Grid>
        <Grid item xs={6} sm={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select value={filters.status} label="Status"
              onChange={(e) => setFilters(p => ({ ...p, status: e.target.value }))}>
              {STATUS_OPTIONS.map(opt => (
                <MenuItem key={opt.value || "all"} value={opt.value || opt}>{opt.label || opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Grade</InputLabel>
            <Select value={filters.grade} label="Grade"
              onChange={(e) => setFilters(p => ({ ...p, grade: e.target.value }))}>
              {GRADE_OPTIONS.map(opt => (
                <MenuItem key={opt.value !== undefined ? opt.value : opt} value={opt.value !== undefined ? opt.value : opt}>
                  {opt.label || opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={6} sm={2}>
          <Button variant="outlined" startIcon={<FilterIcon />} onClick={() => loadApplications()}
            sx={{ height: 40 }}>
            Apply
          </Button>
        </Grid>
        <Grid item xs={6} sm={3} sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button variant="outlined" startIcon={<ExportIcon />} onClick={handleExport}
            sx={{ textTransform: "none", height: 40 }}>
            Export
          </Button>
        </Grid>
      </Grid>

      <Dynamictablepagination columns={columns} rows={rows} page={pagination?.page || 1} totalPages={pagination?.totalPages || 1} handleChangePage={(e, newPage) => loadApplications(newPage)} />

      {selectedApp && (
        <>
          <ApplicationDetailDialog open={detailOpen} onClose={() => setDetailOpen(false)} application={selectedApp} />
          <EvaluationDialog open={evalOpen} onClose={() => setEvalOpen(false)}
            application={selectedApp} onSuccess={handleActionComplete} />
          <AdmissionDialog open={admitOpen} onClose={() => setAdmitOpen(false)}
            application={selectedApp} onSuccess={handleActionComplete} />
        </>
      )}

      <Dialog open={withdrawOpen} onClose={() => setWithdrawOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight="bold">Withdraw Application</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Are you sure you want to withdraw this application?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWithdrawOpen(false)} variant="outlined" sx={{ textTransform: "none" }}>
            Cancel
          </Button>
          <Button onClick={handleWithdraw} variant="contained" sx={{ backgroundColor: "#f44336", textTransform: "none", "&:hover": { backgroundColor: "#d32f2f" } }}>
            Withdraw
          </Button>
        </DialogActions>
      </Dialog>

      <Toast open={toast.open} handleClose={() => setToast({ ...toast, open: false })}
        severity={toast.severity} message={toast.message} />
    </Box>
  );
};

export default AdminRegistrations;
